import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from './index';
import {
  FeatureFlagKey,
  FeatureFlagState,
  FeatureFlagsState,
  FEATURE_FLAG_KEYS,
  FEATURE_FLAG_LABELS,
  FEATURE_FLAG_DEFAULT_REASONS,
} from '../types/featureFlags';
import { getPublicFeatureFlags } from '../services/featureFlagsApi';

type FeatureFlagsSource = 'fetch' | 'connect' | 'setFlag' | 'setAll' | null;

interface FeatureFlagsSliceState {
  /** Полное состояние всех флагов; null — ещё не загружено (optimistic-default: всё считаем включённым). */
  state: FeatureFlagsState | null;
  keys: FeatureFlagKey[];
  labels: Record<FeatureFlagKey, string>;
  loading: boolean;
  error: string | null;
  lastUpdateKey: FeatureFlagKey | null;
  lastSource: FeatureFlagsSource;
  lastUpdatedBy: string | null;
  lastSyncedAt: number | null;
}

const initialState: FeatureFlagsSliceState = {
  state: null,
  keys: FEATURE_FLAG_KEYS,
  labels: FEATURE_FLAG_LABELS,
  loading: false,
  error: null,
  lastUpdateKey: null,
  lastSource: null,
  lastUpdatedBy: null,
  lastSyncedAt: null,
};

export const fetchFeatureFlags = createAsyncThunk(
  'featureFlags/fetch',
  async (_: void, { rejectWithValue }) => {
    try {
      return await getPublicFeatureFlags();
    } catch (err: any) {
      return rejectWithValue(err?.message ?? 'Не удалось загрузить feature-флаги');
    }
  },
);

const featureFlagsSlice = createSlice({
  name: 'featureFlags',
  initialState,
  reducers: {
    setFeatureFlagsState: (
      state,
      action: PayloadAction<{
        state: FeatureFlagsState;
        key?: FeatureFlagKey | null;
        source?: FeatureFlagsSource;
        updatedBy?: string | null;
      }>,
    ) => {
      state.state = action.payload.state;
      state.lastUpdateKey = action.payload.key ?? null;
      state.lastSource = action.payload.source ?? null;
      state.lastUpdatedBy = action.payload.updatedBy ?? null;
      state.lastSyncedAt = Date.now();
      state.error = null;
    },
    setFeatureFlagsMeta: (
      state,
      action: PayloadAction<{
        keys?: FeatureFlagKey[];
        labels?: Record<FeatureFlagKey, string>;
      }>,
    ) => {
      if (action.payload.keys) state.keys = action.payload.keys;
      if (action.payload.labels) state.labels = action.payload.labels;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeatureFlags.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeatureFlags.fulfilled, (state, action) => {
        state.loading = false;
        state.state = action.payload.data.state;
        if (action.payload.data.keys) state.keys = action.payload.data.keys;
        if (action.payload.data.labels) state.labels = action.payload.data.labels;
        state.lastSource = 'fetch';
        state.lastSyncedAt = Date.now();
      })
      .addCase(fetchFeatureFlags.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? action.error.message ?? 'fetch failed';
      });
  },
});

export const { setFeatureFlagsState, setFeatureFlagsMeta } = featureFlagsSlice.actions;
export default featureFlagsSlice.reducer;

// ============== Селекторы ==============

export const selectFeatureFlagsSlice = (state: RootState) => state.featureFlags;
export const selectFeatureFlagsState = (state: RootState) => state.featureFlags.state;
export const selectFeatureFlagsKeys = (state: RootState) => state.featureFlags.keys;
export const selectFeatureFlagsLabels = (state: RootState) => state.featureFlags.labels;
export const selectFeatureFlagsLoading = (state: RootState) => state.featureFlags.loading;
export const selectFeatureFlagsError = (state: RootState) => state.featureFlags.error;
export const selectFeatureFlagsLastUpdateKey = (state: RootState) =>
  state.featureFlags.lastUpdateKey;
export const selectFeatureFlagsLastSource = (state: RootState) =>
  state.featureFlags.lastSource;
export const selectFeatureFlagsLastUpdatedBy = (state: RootState) =>
  state.featureFlags.lastUpdatedBy;
export const selectFeatureFlagsLastSyncedAt = (state: RootState) =>
  state.featureFlags.lastSyncedAt;

export const selectFeatureFlag =
  (key: FeatureFlagKey) =>
  (state: RootState): FeatureFlagState | null =>
    state.featureFlags.state?.[key] ?? null;

/**
 * Optimistic-default: пока состояние не пришло — считаем функцию включённой,
 * чтобы не блокировать UI при разрыве сети.
 */
export const selectIsFeatureEnabled =
  (key: FeatureFlagKey) =>
  (state: RootState): boolean =>
    state.featureFlags.state?.[key]?.enabled !== false;

/** Возвращает текст для пользователя (admin reason ИЛИ дефолтный). */
export const selectFeatureReason =
  (key: FeatureFlagKey) =>
  (state: RootState): string => {
    const reason = state.featureFlags.state?.[key]?.reason;
    if (reason && reason.trim()) return reason;
    return FEATURE_FLAG_DEFAULT_REASONS[key];
  };
